import { useEffect, useRef, useState } from "react";
import { ColorAddedClub } from "../../types/ClubComparison";
import VisibleOFF from "../../assets/icons/VisibleOFF.png";
import Tick from "../../assets/icons/Tick.png";
import SecondaryButton from "../../ui/SecondaryButton";
import LabelText from "../../ui/LabelText";
import { FilterOption } from "../PlayerComparison/ScattrPlotUtils";
import SortArrowUp from "../../assets/icons/sort_arrow_asc.png";
import SortArrowDown from "../../assets/icons/sort_arrow_desc.png";
import SortArrowLeft from "../../assets/icons/sort-arrow-left.png";
import SortArrowRigt from "../../assets/icons/sort-arrow-right.png";
import ClubCard from "../../ui/CardComponent/ClubCard";

export type IPossibleClubFilter =
  | "teamTotalRevenue"
  | "teamTotalCosts"
  | "teamTotalProfitLoss"
  | "teamTotalDebt"
  | "teamAssetsLiabilities"
  | "teamTradingEfficiency"
  | "teamAverageSystemFit"
  | "teamAverageSquadForm"
  | "teamAveragePlayerAge"
  | "teamAverageSquadValue"
  | "finishingShots"
  | "finishingAerial"
  | "attackPositioning"
  | "creativity"
  | "passingAccuracy"
  | "dribbling"
  | "crossing"
  | "tackling"
  | "defensiveHeaders"
  | "aggression"
  | "totalXgShot"
  | "totalXgShotAgainst"
  | "ppg"
  | null;

export interface IClubFilter {
  up: IPossibleClubFilter;
  across: IPossibleClubFilter;
  dotSize: IPossibleClubFilter;
}

export const ClubPlotFilterOptions = [
  { label: "Revenue", value: "teamTotalRevenue", textKey: "teamTotalRevenueText" },
  { label: "Costs", value: "teamTotalCosts", textKey: "teamTotalCostsText" },
  { label: "Profit/loss", value: "teamTotalProfitLoss", textKey: "teamTotalProfitLossText" },
  { label: "Debt", value: "teamTotalDebt", textKey: "teamTotalDebtText" },
  // { label: "Assets/liabilities", value: "teamAssetsLiabilities", textKey: "teamAssetsLiabilitiesText" },
  // { label: "Trading Efficiency", value: "teamTradingEfficiency" },
  { label: "Av. System fit", value: "teamAverageSystemFit" },
  { label: "League Position", value: "currentLeaguePosition" },
  // { label: "Av. Squad Form", value: "teamAverageSquadForm" },
  { label: "Av. Player age", value: "teamAveragePlayerAge" },
  { label: "Av. Player value", value: "teamAverageSquadValue" },
  { label: "Av. player wages", value: "avWagePerWeek" },
  { label: "Finishing - Shots", value: "finishingShots" },
  { label: "Finishing - Aerial", value: "finishingAerial" },
  { label: "Attack positioning", value: "attackPositioning" },
  { label: "Creativity", value: "creativity" },
  { label: "Passing accuracy", value: "passingAccuracy" },
  { label: "Dribbling", value: "dribbling" },
  { label: "Crossing", value: "crossing" },
  { label: "Tackling", value: "tackling" },
  { label: "Defensive headers", value: "defensiveHeaders" },
  { label: "Ball Recovery", value: "aggression" },
  { label: "XG Per 90", value: "totalXgShot" },
  { label: "XG Against Per 90", value: "totalXgShotAgainst" },
  { label: "PPG", value: "ppg" },


];

export const separateStringByCapitalLetters = (str: string) => {
  const words = str?.split(/(?=[A-Z])/);
  const capitalizedWords = words?.map((word) => {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
  const result = capitalizedWords?.join(" ");
  return result;
};

interface ClubPlotFilterProps {
  clubPlotFilters: IClubFilter;
  setSelectedPlotFilterOption: React.Dispatch<React.SetStateAction<"up" | "across" | "dotSize" | undefined>>;
  selectedPlotFilterOption: "up" | "across" | "dotSize" | undefined;
  setPlotFilters: React.Dispatch<React.SetStateAction<IClubFilter>>;
  acrossSortDirection: "inc" | "dec";
  setAcrossSortDirection: React.Dispatch<React.SetStateAction<"inc" | "dec">>;
  upSortDirection: "desc" | "asc";
  setUpSortDirection: React.Dispatch<React.SetStateAction<"desc" | "asc">>;
}

export const PlotClubFilters: React.FC<ClubPlotFilterProps> = ({
  clubPlotFilters,
  setSelectedPlotFilterOption,
  selectedPlotFilterOption,
  setPlotFilters,
  acrossSortDirection,
  setAcrossSortDirection,
  upSortDirection,
  setUpSortDirection,
}) => {
  const handleFilterOptionChange = (option: { label: string; value: string }) => {
    clubPlotFilters[selectedPlotFilterOption!] === option?.value
      ? setPlotFilters((prev) => ({ ...prev, [selectedPlotFilterOption!]: null }))
      : setPlotFilters((prev) => ({ ...prev, [selectedPlotFilterOption!]: option?.value }));
    const { up, across, dotSize } = clubPlotFilters;
    const isDuplicate = up === option.value || across === option.value || dotSize === option.value;
    if (isDuplicate) {
      setPlotFilters((prev) => ({
        ...prev,
        [up === option.value ? "up" : across === option.value ? "across" : "dotSize"]: null,
      }));
    }
    setSelectedPlotFilterOption(undefined);
  };

  const handleUpSortClick = (event: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
    event.stopPropagation();
    if (upSortDirection === "asc") {
      setUpSortDirection("desc");
    } else {
      setUpSortDirection("asc");
    }
  };

  const handleAcrossSortClick = (event: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
    event.stopPropagation();
    if (acrossSortDirection === "inc") {
      setAcrossSortDirection("dec");
    } else {
      setAcrossSortDirection("inc");
    }
  };

  return (
    <div className="comparison-tool-btns-view">
      {Object.keys(clubPlotFilters).map((option) => (
        <SecondaryButton
          key={option}
          label={
            <>
              <span className="sort-label">{separateStringByCapitalLetters(option)}: </span>
              {clubPlotFilters[option as FilterOption] ? (
                <LabelText className="clear-labeltext">
                  {ClubPlotFilterOptions.find((opt) => opt.value === clubPlotFilters[option as FilterOption])?.label || "--"}
                </LabelText>
              ) : (
                "--"
              )}
              {option === "up" && clubPlotFilters[option as FilterOption] && (
                <img
                  className="plot-filter-icons"
                  onClick={(event) => handleUpSortClick(event)}
                  src={upSortDirection === "asc" ? SortArrowUp : SortArrowDown}
                  alt=""
                />
              )}
              {option === "across" && clubPlotFilters[option as FilterOption] && (
                <img
                  onClick={(event) => handleAcrossSortClick(event)}
                  className="plot-filter-icons across-sort-icon"
                  src={acrossSortDirection === "inc" ? SortArrowRigt : SortArrowLeft}
                  alt=""
                />
              )}
            </>
          }
          className={clubPlotFilters[option as FilterOption] ? "" : "plot-filter-inactive"}
          onClick={() =>
            selectedPlotFilterOption === option
              ? setSelectedPlotFilterOption(undefined)
              : setSelectedPlotFilterOption(option as FilterOption)
          }
        />
      ))}
      {selectedPlotFilterOption && (
        <div className="plot-filter-items">
          <div>{selectedPlotFilterOption?.toUpperCase()}</div>
          {ClubPlotFilterOptions?.map((option) => (
            <div
              className="plot-filter-option"
              style={{ backgroundColor: clubPlotFilters[selectedPlotFilterOption!] === option?.value ? "#F2F5EE" : "" }}
              onClick={() => handleFilterOptionChange(option)}
            >
              {" "}
              {option?.label}
              {clubPlotFilters[selectedPlotFilterOption!] === option?.value && (
                <>
                  {selectedPlotFilterOption === "up" && (
                    <img
                      onClick={(event) => handleUpSortClick(event)}
                      src={upSortDirection === "asc" ? SortArrowUp : SortArrowDown}
                      alt=""
                    />
                  )}
                  {selectedPlotFilterOption === "across" && (
                    <img
                      onClick={(event) => handleAcrossSortClick(event)}
                      src={acrossSortDirection === "inc" ? SortArrowRigt : SortArrowLeft}
                      alt=""
                      className="plot-filter-option-icon"
                    />
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const generateRandomUniqueColors = (count: number) => {
  const colors: string[] = [];
  const hexChars = "0123456789ABCDEF";
  while (colors.length < count) {
    let color = "#";
    for (let i = 0; i < 6; i++) {
      color += hexChars[Math.floor(Math.random() * 16)];
    }
    if (!colors.includes(color) && color !== "#4AF075") {
      colors.push(color);
    }
  }
  return colors;
};

const formatPlayerCode = (playerLabel: string): string => {
  if (!playerLabel) return "";
  const nameParts = playerLabel.split(" ");
  if (nameParts.length === 1) {
    return nameParts[0].slice(0, 4).toUpperCase();
  }
  const lastName = nameParts[nameParts.length - 1];
  const firstInitial = nameParts[0][0];
  const lastNamePart = lastName.slice(0, 3);
  return (firstInitial + lastNamePart).toUpperCase();
};

interface ClubSiderbarListProps {
  colorsAddedClubs: ColorAddedClub[];
  hoveredClub: number;
  setHoveredClub: React.Dispatch<React.SetStateAction<number>>;
  setSelectedClubs: React.Dispatch<React.SetStateAction<number[]>>;
  selectedClubs: number[];
  selectedSimilarClub: ColorAddedClub | null;
  hiddenClubs: number[];
  setHiddenClubs: React.Dispatch<React.SetStateAction<number[]>>;
  isChunkActive: boolean;
  isNickName: boolean;
  clubCardId: number;
  setClubCardId: React.Dispatch<React.SetStateAction<number>>;
}

interface IClubTab {
  clubID: number;
  clubImageURL: string;
  clubLabel: string;
  selectedClubs: number[];
  hiddenClubs: number[];
  handleSelectedClub: (event: React.MouseEvent<HTMLDivElement, MouseEvent>, clubId: number) => void;
  isNickName: boolean;
}

const ClubTab: React.FC<IClubTab> = ({ clubID, clubImageURL, clubLabel, selectedClubs, hiddenClubs, handleSelectedClub, isNickName }) => {
  return (
    <>
      <div>
        <div className="player-tab-img-container">
          {!selectedClubs?.includes(clubID) && !hiddenClubs?.includes(clubID) && (
            <div className="plot-player-image" onClick={(event) => handleSelectedClub(event, clubID)}>
              <img src={clubImageURL} alt="club-image" />
            </div>
          )}
          {hiddenClubs?.includes(clubID) && (
            <div className="plot-player-image-hidden" onClick={(event) => handleSelectedClub(event, clubID)}>
              <img src={VisibleOFF} alt="hidden-player" />
            </div>
          )}
          {selectedClubs?.includes(clubID) && !hiddenClubs?.includes(clubID) && (
            <div className="plot-player-image-selected" onClick={(event) => handleSelectedClub(event, clubID)}>
              <img src={Tick} className="plot-tick-mark" alt="selected-mark" />
            </div>
          )}
        </div>
        <div className="player-tab-name">
          <p className={hiddenClubs?.includes(clubID) ? "hidden-player" : ""}>{clubLabel}</p>
          {isNickName && <span>{formatPlayerCode(clubLabel)}</span>}
        </div>
      </div>
    </>
  );
};

export const PlotClubSiderbarList: React.FC<ClubSiderbarListProps> = ({
  colorsAddedClubs,
  hoveredClub,
  setHoveredClub,
  setSelectedClubs,
  selectedClubs,
  selectedSimilarClub,
  hiddenClubs,
  setHiddenClubs,
  isChunkActive,
  isNickName,
  clubCardId,
  setClubCardId,
}) => {
  const handleSelectedClub = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, clubId: number) => {
    event.stopPropagation();
    if (selectedClubs?.includes(clubId)) {
      const newSelectedClubs = selectedClubs?.filter((clubID) => clubID !== clubId);
      setSelectedClubs(newSelectedClubs);
    } else if (hiddenClubs?.includes(clubId)) {
      const newHiddenClubs = hiddenClubs?.filter((clubID) => clubID !== clubId);
      setHiddenClubs(newHiddenClubs);
    } else {
      setSelectedClubs((prev) => [...prev, clubId]);
    }
  };
  const clubRef = useRef<HTMLDivElement>(null);
  const clubDetailsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (hoveredClub && clubRef.current) {
      clubRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [hoveredClub]);
  useEffect(() => {
    if (clubCardId && clubDetailsRef.current) {
      clubDetailsRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [clubCardId]);
  return (
    <div className={`${selectedSimilarClub ? "plot-player-list-benchmarked" : ""} plot-player-list`}>
      {colorsAddedClubs
        ?.filter((club) => club?.teamId !== selectedSimilarClub?.teamId)
        ?.map((club) => (
          <>
            {isChunkActive && hiddenClubs.includes(club?.teamId) ? null : (
              <>
                {clubCardId !== club?.teamId && (
                  <div
                    key={club?.teamId}
                    ref={hoveredClub === club?.teamId ? clubRef : null}
                    className={`plot-player-tab ${hoveredClub === club?.teamId ? "plot-player-tab-hovered" : ""}`}
                    onMouseOver={
                      hiddenClubs?.includes(club?.teamId)
                        ? () => {}
                        : () => {
                            setHoveredClub(club?.teamId);
                          }
                    }
                    onMouseOut={
                      hiddenClubs?.includes(club?.teamId)
                        ? () => {}
                        : () => {
                            setHoveredClub(0);
                          }
                    }
                    onClick={() => (clubCardId === club?.teamId ? setClubCardId(0) : setClubCardId(club?.teamId))}
                  >
                    <ClubTab
                      clubID={club?.teamId}
                      clubImageURL={club?.teamLogoURL}
                      clubLabel={club?.teamLabel}
                      selectedClubs={selectedClubs}
                      hiddenClubs={hiddenClubs}
                      handleSelectedClub={handleSelectedClub}
                      isNickName={isNickName}
                    />
                    <span
                      style={{
                        backgroundColor: selectedClubs?.includes(club?.teamId) ? "#4AF075" : club?.colorCode,
                        width: hoveredClub > 0 ? (hoveredClub === club?.teamId ? "16px" : "8px") : "16px",
                        height: hoveredClub > 0 ? (hoveredClub === club?.teamId ? "16px" : "8px") : "16px",
                        border: false ? (selectedClubs?.includes(club?.teamId) ? "" : "2px solid #00D1A8") : "",
                        filter: false ? "drop-shadow(2px 2px 8px rgba(0, 0, 0, 0.40))" : "",
                      }}
                    ></span>
                  </div>
                )}
                {clubCardId === club?.teamId && (
                  <div ref={clubDetailsRef}>
                    <ClubCard
                      clubId={club?.teamId}
                      clubName={club?.teamLabel}
                      leagueName={club?.competitionName}
                      clubImageURL={club?.teamLogoURL}
                      revenue={club?.teamTotalRevenueText}
                      costs={club?.teamTotalCostsText}
                      profitLoss={club?.teamTotalProfitLossText}
                      debt={club?.teamTotalDebtText}
                      assetsLiabilities={club?.teamAssetsLiabilitiesText}
                      currentLeaguePosition={club?.currentLeaguePosition}
                      currentExpectedLeaguePosition={club?.currentExpectedLeaguePosition}
                      setClubCardID={setClubCardId}
                    />
                  </div>
                )}
              </>
            )}
          </>
        ))}
    </div>
  );
};
